import {matchRoutes} from "react-router-dom";
import {screenCatalog} from "./screenCatalog.generated";

export interface IMatchedScreen {
  /** GA4 screen_name (human-readable). */
  screenName: string;
  /** GA4 screen_class (stable catalog id). */
  screenClass: string;
}

// Look up the catalog entry for a matched route pattern. Each route_path maps to
// a single screen in the catalog, so keying by the pattern string is sufficient.
const entryByRoutePath = new Map(screenCatalog.map((entry) => [entry.routePath, entry]));

// Route objects fed to React Router's matcher. matchRoutes ranks candidates by
// specificity (static > dynamic > splat), so the most specific catalog entry wins.
const routeObjects = screenCatalog.map((entry) => ({path: entry.routePath}));

/**
 * Resolves a concrete location path (e.g. "/songs/browse") to its screen-catalog
 * entry, returning the GA4 screen_name / screen_class. Returns null when no
 * catalog entry matches, in which case callers should not emit a screen view.
 */
export function matchScreen(locationPath: string): IMatchedScreen | null {
  const normalized = normalizePath(locationPath);

  // The Songs Home tab is reached at /songs/home. React Router ranks the dynamic
  // /songs/:songId (Song Details) pattern above the /songs/* splat, so without
  // this guard /songs/home would be mis-resolved to Song Details.
  if (normalized === "/songs/home") {
    const home = entryByRoutePath.get("/songs");
    return home ? {screenName: home.productName, screenClass: home.id} : null;
  }

  const matches = matchRoutes(routeObjects, {pathname: normalized});
  if (!matches || matches.length === 0) {
    return null;
  }

  const matchedPath = matches[matches.length - 1].route.path;
  const entry = matchedPath !== undefined ? entryByRoutePath.get(matchedPath) : undefined;
  if (!entry) {
    return null;
  }
  return {screenName: entry.productName, screenClass: entry.id};
}

/**
 * Strips a trailing slash (except for the root) so "/songs/browse/" and
 * "/songs/browse" resolve identically.
 */
function normalizePath(locationPath: string): string {
  if (locationPath.length > 1 && locationPath.endsWith("/")) {
    return locationPath.replace(/\/+$/, "");
  }
  return locationPath;
}
